/**
 * Query + mutation hooks for firm communications (#542) — Portal Messages
 * threads read through the CC worker's proxy routes.
 *
 *   Slice 1 (case panel):
 *   useCaseThreads(leadId, enabled) → GET   /api/operations/messages/case/:leadId
 *   useReplyToThread(leadId)        → POST  /api/operations/messages/:threadId/reply
 *   useMarkThreadRead(leadId)       → PATCH /api/operations/messages/:threadId/read
 *
 *   Slice 2 (global Messages view):
 *   useGlobalThreads()              → GET   /api/operations/messages
 *   useCaseComposeTargets(leadId)   → GET   /api/operations/messages/case/:leadId/targets
 *   useComposeCaseMessage()         → POST  /api/operations/messages/case
 *   useComposeFirmMessage()         → POST  /api/operations/messages/firm
 *
 * Key space: ["firm-messages", "case", leadId] per matter; ["firm-messages",
 * "global"] for the global view. Mutations invalidate both — a reply,
 * read-mark, or compose must reconcile whichever surface is open. Compose
 * invalidates the whole ["firm-messages"] family: a new case thread belongs
 * to some case key, and blanket invalidation beats tracking which.
 *
 * The store is the portal's; a reply or compose also emails the firm and
 * mirrors to Slack (portal-side behavior — CC just calls the door). The reply
 * route chains a mark-read server-side, so a reply's invalidation refreshes
 * `unread` without a second client call.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CaseComposeTargetsResponseSchema,
  CaseThreadsResponseSchema,
  GlobalThreadsResponseSchema,
  type CaseComposeTargetsResponse,
  type CaseThreadsResponse,
  type ComposeCaseMessageRequest,
  type ComposeCaseMessageResponse,
  type ComposeFirmMessageRequest,
  type ComposeFirmMessageResponse,
  type MarkThreadReadResponse,
  type ReplyMessageResponse,
} from "@command-center/shared";
import { apiGet, apiPatch, apiPost } from "./api";
import { createPollingQuery } from "./create-polling-query";

const FAMILY_KEY = ["firm-messages"] as const;
const caseKey = (leadId: string) => ["firm-messages", "case", leadId] as const;
const GLOBAL_KEY = ["firm-messages", "global"] as const;

/** A matter's firm-message threads. Fetched only while the section is
 *  rendered (`enabled`) — the panel gates on `leadRecordId` being present. */
export function useCaseThreads(leadId: string, enabled: boolean) {
  return useQuery<CaseThreadsResponse, Error>({
    queryKey: caseKey(leadId),
    enabled,
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        `/api/operations/messages/case/${encodeURIComponent(leadId)}`,
      );
      const parsed = CaseThreadsResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `/api/operations/messages/case/:leadId failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
  });
}

/**
 * Whether a failed reply may already be in the thread (#1355): the reply
 * route's failure body carries `saved` — false when nothing reached the
 * thread, "unknown" when it may have saved. Read off the error's `body`
 * (ServerError and ClientError both keep it), never by class. A 4xx with no
 * `saved` (the auth middleware's 401, before the route runs) is false: it was
 * refused before anything was posted. Anything else without a `saved` — a
 * browser fetch that never got an answer, an edge 5xx — is undefined, and the
 * caller treats it as may-have-saved. The compose routes (/case, /firm) answer
 * by the same rules since #1357, so the compose box reads it too.
 */
export function replySavedOf(err: unknown): false | "unknown" | undefined {
  const e = err as { body?: unknown; status?: unknown } | null | undefined;
  const body = e?.body;
  if (body !== null && typeof body === "object") {
    const saved = (body as { saved?: unknown }).saved;
    if (saved === false || saved === "unknown") return saved;
  }
  const status = e?.status;
  if (typeof status === "number" && status >= 400 && status < 500) return false;
  return undefined;
}

/** LF reply into a thread (the portal emails the firm + mirrors to Slack).
 *  Family-wide invalidation: the shared thread list renders on both surfaces
 *  (case panel + global view), and a reply must reconcile whichever else is
 *  mounted — the family prefix covers every case key and the global key.
 *  A failure that may have saved refreshes too, so the operator reading the
 *  thread sees the reply if it landed. */
export function useReplyToThread() {
  const queryClient = useQueryClient();
  return useMutation<ReplyMessageResponse, Error, { threadId: string; body: string }>({
    mutationFn: ({ threadId, body }) =>
      apiPost<ReplyMessageResponse>(
        `/api/operations/messages/${encodeURIComponent(threadId)}/reply`,
        { body },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FAMILY_KEY });
    },
    onError: (err) => {
      if (replySavedOf(err) !== false) {
        void queryClient.invalidateQueries({ queryKey: FAMILY_KEY });
      }
    },
  });
}

/** Mark a thread read-by-LF (fired once when the operator expands it). */
export function useMarkThreadRead() {
  const queryClient = useQueryClient();
  return useMutation<MarkThreadReadResponse, Error, { threadId: string }>({
    mutationFn: ({ threadId }) =>
      apiPatch<MarkThreadReadResponse>(
        `/api/operations/messages/${encodeURIComponent(threadId)}/read`,
        {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FAMILY_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// Slice 2 — the global Messages view
// ---------------------------------------------------------------------------

/** Every thread, in the worker's two lanes (case-tagged / firm-level), each
 *  already sorted unread-first. Polls every 30s — the page's unread badging is
 *  a watch signal, and the fetch-all corpus is small (~150 threads). */
export const useGlobalThreads = createPollingQuery({
  queryKey: GLOBAL_KEY,
  path: "/api/operations/messages",
  schema: GlobalThreadsResponseSchema,
  refetchIntervalMs: 30_000,
});

/** The compose case picker's second hop: a picked lead's routing legs
 *  (placement ids + HF firm names) so the operator addresses the right leg. */
export function useCaseComposeTargets(leadId: string | null) {
  return useQuery<CaseComposeTargetsResponse, Error>({
    queryKey: ["firm-messages", "compose-targets", leadId],
    enabled: leadId !== null,
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        `/api/operations/messages/case/${encodeURIComponent(leadId!)}/targets`,
      );
      const parsed = CaseComposeTargetsResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `/api/operations/messages/case/:leadId/targets failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
  });
}

/** Start a case-tagged thread (the portal emails the side firm's default
 *  recipients + mirrors to Slack). Invalidates the whole firm-messages family
 *  — the new thread belongs to some case key as well as the global view. */
export function useComposeCaseMessage() {
  const queryClient = useQueryClient();
  return useMutation<ComposeCaseMessageResponse, Error, ComposeCaseMessageRequest>({
    mutationFn: (req) =>
      apiPost<ComposeCaseMessageResponse>("/api/operations/messages/case", req),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FAMILY_KEY });
    },
    // A compose that may have saved refreshes the threads too (#1357), so the
    // operator reading them sees the new thread if it landed.
    onError: (err) => {
      if (replySavedOf(err) !== false) {
        void queryClient.invalidateQueries({ queryKey: FAMILY_KEY });
      }
    },
  });
}

/** Start a firm-level thread to one contact (visibility "Firm" = every
 *  contact at the firm sees it; "Direct" = recipient + LF only). */
export function useComposeFirmMessage() {
  const queryClient = useQueryClient();
  return useMutation<ComposeFirmMessageResponse, Error, ComposeFirmMessageRequest>({
    mutationFn: (req) =>
      apiPost<ComposeFirmMessageResponse>("/api/operations/messages/firm", req),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FAMILY_KEY });
    },
    // A compose that may have saved refreshes the threads too (#1357), so the
    // operator reading them sees the new thread if it landed.
    onError: (err) => {
      if (replySavedOf(err) !== false) {
        void queryClient.invalidateQueries({ queryKey: FAMILY_KEY });
      }
    },
  });
}
